import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Button, Alert, Typography } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUniversity } from '@fortawesome/free-solid-svg-icons';
import { CheckCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';

const { Text } = Typography;

interface RibModalProps {
  visible: boolean;
  onCancel: () => void;
  onConfirm: (rib: string, bic?: string) => void | Promise<void>;
  loading?: boolean;
  requireBic?: boolean;
  // allow controlling z-index from parent to avoid stacking issues with other modal libraries
  zIndex?: number;
}

const validateRibFormat = (ribValue: string): boolean => {
  const cleanRib = ribValue.replace(/\s/g, '');
  return cleanRib.length === 23 && /^[A-Z0-9]{23}$/i.test(cleanRib);
};

const validateBicFormat = (bicValue: string): boolean => {
  if (!bicValue) return false;
  const cleanBic = bicValue.replace(/\s/g, '').toUpperCase();
  return (
    (cleanBic.length === 8 || cleanBic.length === 11) &&
    /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(cleanBic)
  );
};

const formatRibDisplay = (value: string): string => {
  const cleaned = value.replace(/\s/g, '').toUpperCase();
  if (cleaned.length <= 5) return cleaned;
  if (cleaned.length <= 10) return `${cleaned.slice(0, 5)} ${cleaned.slice(5)}`;
  if (cleaned.length <= 21)
    return `${cleaned.slice(0, 5)} ${cleaned.slice(5, 10)} ${cleaned.slice(10)}`;
  return `${cleaned.slice(0, 5)} ${cleaned.slice(5, 10)} ${cleaned.slice(10, 21)} ${cleaned.slice(21)}`;
};

const formatBicDisplay = (value: string): string => {
  const cleaned = value.replace(/\s/g, '').toUpperCase();
  if (cleaned.length <= 4) return cleaned;
  if (cleaned.length <= 6) return `${cleaned.slice(0, 4)} ${cleaned.slice(4)}`;
  if (cleaned.length <= 8) return `${cleaned.slice(0, 4)} ${cleaned.slice(4, 6)} ${cleaned.slice(6)}`;
  return `${cleaned.slice(0, 4)} ${cleaned.slice(4, 6)} ${cleaned.slice(6, 8)} ${cleaned.slice(8)}`;
};

const RibModal: React.FC<RibModalProps> = ({
  visible,
  onCancel,
  onConfirm,
  loading = false,
  requireBic = false,
  zIndex = 12010,
}) => {
  const [form] = Form.useForm();
  const [ribValue, setRibValue] = useState('');
  const [bicValue, setBicValue] = useState('');
  const [ribError, setRibError] = useState('');
  const [bicError, setBicError] = useState('');

  useEffect(() => {
    if (!visible) {
      form.resetFields();
      setRibValue('');
      setBicValue('');
      setRibError('');
      setBicError('');
    }
  }, [visible, form]);

  const handleRibChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^A-Z0-9]/gi, '').toUpperCase().slice(0, 23);
    setRibValue(value);
    form.setFieldsValue({ rib: formatRibDisplay(value) });

    if (value.length > 0 && value.length < 23) {
      setRibError(`Le RIB doit contenir 23 caractères (${value.length}/23)`);
    } else if (value.length === 23 && !validateRibFormat(value)) {
      setRibError('Format de RIB invalide');
    } else {
      setRibError('');
    }
  };

  const handleBicChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^A-Z0-9]/gi, '').toUpperCase().slice(0, 11);
    setBicValue(value);
    form.setFieldsValue({ bic: formatBicDisplay(value) });

    if (value.length > 0 && value.length < 8) {
      setBicError(`Le BIC doit contenir au moins 8 caractères (${value.length}/8)`);
    } else if (value.length > 8 && value.length < 11) {
      setBicError(`Le BIC doit contenir 8 ou 11 caractères (${value.length})`);
    } else if ((value.length === 8 || value.length === 11) && !validateBicFormat(value)) {
      setBicError('Format de BIC invalide');
    } else {
      setBicError('');
    }
  };

  const handleSubmit = async () => {
    if (!validateRibFormat(ribValue)) {
      setRibError('RIB invalide - veuillez vérifier le format');
      return;
    }

    // Si requireBic === true, le BIC est obligatoire ; sinon il est optionnel mais s'il est saisi il doit être valide
    if (requireBic) {
      if (!validateBicFormat(bicValue)) {
        setBicError('BIC invalide - veuillez vérifier le format');
        return;
      }
    } else {
      if (bicValue && !validateBicFormat(bicValue)) {
        setBicError('BIC invalide - veuillez vérifier le format');
        return;
      }
    }

    // appeler onConfirm ; accepter sync ou async
    await Promise.resolve(onConfirm(ribValue, bicValue || undefined));
  };

  const isValid =
    validateRibFormat(ribValue) &&
    (requireBic ? validateBicFormat(bicValue) : (bicValue ? validateBicFormat(bicValue) : true));

  return (
    <Modal
      title={
        <span style={{ color: '#2A919D' }}>
          <FontAwesomeIcon icon={faUniversity} className="me-2" />
          Coordonnées bancaires
        </span>
      }
      open={visible}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={loading}>
          Annuler
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleSubmit}
          loading={loading}
          disabled={!isValid}
          style={{ backgroundColor: '#4BB0C0', borderColor: '#4BB0C0' }}
        >
          Confirmer
        </Button>,
      ]}
      width={600}
      zIndex={zIndex}
      centered
    >
      <Alert
        message="Informations bancaires requises"
        description={
          requireBic
            ? 'Veuillez renseigner votre RIB (23 caractères) et votre code BIC (8 ou 11 caractères).'
            : 'Veuillez renseigner votre RIB (23 caractères). Le code BIC est optionnel.'
        }
        type="info"
        showIcon
        style={{ marginBottom: '24px', color: '#2A919D' }}
        icon={<InfoCircleOutlined style={{ color: '#2A919D', fontSize: '20px' }} />}
      />

      <Form form={form} layout="vertical">
        <Form.Item
          label={<strong>RIB (23 caractères) *</strong>}
          validateStatus={ribError ? 'error' : ribValue.length === 23 ? 'success' : ''}
          help={ribError || (ribValue.length === 23 && 'RIB valide ✓')}
        >
          <Input
            placeholder="Ex: FR76 1234 5678 9012 3456 789 01"
            value={form.getFieldValue('rib')}
            onChange={handleRibChange}
            maxLength={29}
            style={{ fontFamily: 'Mulish', fontSize: '16px', textTransform: 'uppercase' }}
            disabled={loading}
          />
          <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '8px' }}>
            Format: FR76 1234 5678 9012 3456 789 01 (23 caractères alphanumériques)
          </Text>
        </Form.Item>

        {requireBic && (
          <Form.Item
            label={<strong>Code BIC/SWIFT (8 ou 11 caractères) *</strong>}
            validateStatus={bicError ? 'error' : (bicValue.length === 8 || bicValue.length === 11) ? 'success' : ''}
            help={bicError || ((bicValue.length === 8 || bicValue.length === 11) && 'BIC valide ✓')}
          >
            <Input
              placeholder="Ex: BNPAFRPPXXX ou BNPAFRPP"
              value={form.getFieldValue('bic')}
              onChange={handleBicChange}
              maxLength={14}
              style={{ fontFamily: 'Mulish', fontSize: '16px', textTransform: 'uppercase' }}
              disabled={loading}
            />
            <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '8px' }}>
              Format: AAAA BB CC XXX (4 lettres + 2 lettres + 2 alphanumériques + 3 optionnels)
            </Text>
          </Form.Item>
        )}
      </Form>

      <Alert
        message="Sécurité"
        description="Vos coordonnées bancaires sont stockées de manière sécurisée et uniquement utilisées pour le remboursement."
        showIcon
        style={{
          marginBottom: '16px',
          borderColor: '#4BB0C0',
          backgroundColor: '#e6f7f9',
          color: '#2c2c2b',
        }}
        icon={<CheckCircleOutlined style={{ color: '#4BB0C0', fontSize: '20px' }} />}
      />
    </Modal>
  );
};

export default RibModal;